import { useEffect, useState } from 'react';
import type { EntityKind } from '@/api/base-types';
import { entityPath, useBaseMutation, useClientPick, useMergePreview, useStudioList, useTeamList } from '@/api/base';
import { api } from '@/api/client';
import { Button } from '@/components/kit/Button';
import { Option } from '@/components/kit/Controls';
import { TextField } from '@/components/kit/Field';
import { Modal } from '@/components/kit/Modal';
import { Note, Skeleton } from '@/components/kit/Note';
import { FieldHint, OptionList } from '@/components/kit/OptionList';
import { useToast } from '@/components/kit/Toast';
import { clientDescription } from './ClientPicker';
import { errorText } from './useUndoable';

const WHAT: Record<EntityKind, { find: string; other: string }> = {
  client: { find: 'вторую карточку клиента', other: 'Другая карточка' },
  studio: { find: 'вторую студию', other: 'Другая запись' },
  team: { find: 'вторую команду', other: 'Другая запись' },
};

/**
 * Слить с дублем (БЛ 5.5, fMerge в макете). Шаг 1 — найти дубль.
 * Шаг 2 — сравнение: равные значения в одну ячейку, расходящиеся —
 * выбором; связи обеих записей; галочка «нельзя отменить».
 * Остаётся эта запись, дубль исчезает.
 */
export function MergeModal({
  entity,
  id,
  preselect,
  onClose,
  onMerged,
}: {
  entity: EntityKind;
  id: string;
  preselect?: string | null;
  onClose: () => void;
  onMerged?: () => void;
}) {
  const toast = useToast();
  const [otherId, setOtherId] = useState<string | null>(preselect ?? null);
  const [picked, setPicked] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [fields, setFields] = useState<Record<string, 'a' | 'b'>>({});
  const [ok, setOk] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setQ(text), 250);
    return () => window.clearTimeout(t);
  }, [text]);

  const clients = useClientPick(entity === 'client' && !otherId ? q : '', [id]);
  const teams = useTeamList({ q, limit: 6 }, entity === 'team' && !otherId && q.length >= 2);
  const studios = useStudioList({ q, limit: 6 }, entity === 'studio' && !otherId && q.length >= 2);
  const preview = useMergePreview(entity, id, otherId);
  const merge = useBaseMutation(() => api.post(`${entityPath(entity, id)}/merge`, { otherId, fields }));

  // По умолчанию — более длинное значение, как в макете
  useEffect(() => {
    if (!preview.data) return;
    const init: Record<string, 'a' | 'b'> = {};
    for (const f of preview.data.fields) {
      if (f.a !== f.b) init[f.key] = (f.a ?? '').length >= (f.b ?? '').length ? 'a' : 'b';
    }
    setFields(init);
    setOk(false);
  }, [preview.data]);

  if (!otherId) {
    const options =
      entity === 'client'
        ? (clients.data ?? []).map((c) => ({ value: c.id, title: c.fullName, description: clientDescription(c) }))
        : entity === 'team'
          ? (teams.data?.items ?? []).filter((t) => t.id !== id).map((t) => ({ value: t.id, title: t.name, description: t.city.name }))
          : (studios.data?.items ?? []).filter((s) => s.id !== id).map((s) => ({ value: s.id, title: s.name, description: s.city.name }));
    return (
      <Modal
        title="Слить с дублем"
        size="wide"
        onClose={onClose}
        confirm={{ label: 'Сравнить', onClick: () => setOtherId(picked), disabled: !picked }}
      >
        <p>Найдите {WHAT[entity].find} — дубль этой. Все её связи перейдут сюда, а сама она исчезнет.</p>
        <TextField
          label="Поиск"
          placeholder={entity === 'client' ? 'ФИО или телефон' : 'название или город'}
          value={text}
          onValue={setText}
          autoFocus
        />
        {q.length < 2 ? <FieldHint gap>Начните вводить — покажем совпадения.</FieldHint> : null}
        <OptionList value={picked} onChange={setPicked} options={options} />
      </Modal>
    );
  }

  const p = preview.data;
  const submit = () =>
    merge.mutate(undefined, {
      onSuccess: () => {
        toast.show('Дубли слиты');
        onClose();
        onMerged?.();
      },
      onError: (e) => toast.showError(errorText(e)),
    });

  return (
    <Modal
      title="Слить с дублем"
      size="xl"
      onClose={onClose}
      footer={
        <>
          <Button tone="danger" disabled={!ok || !p || merge.isPending} onClick={submit}>
            Слить
          </Button>
          <Button onClick={onClose}>Отмена</Button>
          <span className="sp" />
          <Button tone="ghost" onClick={() => setOtherId(null)}>
            {WHAT[entity].other}
          </Button>
        </>
      }
    >
      {preview.isError ? (
        <Note tone="crit">{preview.error.message}</Note>
      ) : !p ? (
        <Skeleton width="70%" />
      ) : (
        <>
          <table className="tbl mtbl">
            <thead>
              <tr>
                <th>Поле</th>
                <th>{entity === 'client' ? 'Эта карточка' : 'Эта запись'} · остаётся</th>
                <th>Дубль · исчезнет</th>
              </tr>
            </thead>
            <tbody>
              {p.fields.map((f) =>
                f.a === f.b ? (
                  <tr key={f.key}>
                    <td className="muted">{f.label}</td>
                    <td colSpan={2}>{f.a ?? <span className="na">—</span>}</td>
                  </tr>
                ) : (
                  <tr key={f.key}>
                    <td className="muted">{f.label}</td>
                    {(['a', 'b'] as const).map((side) => (
                      <td key={side}>
                        <Option
                          radio
                          checked={fields[f.key] === side}
                          onToggle={() => setFields({ ...fields, [f.key]: side })}
                          title={f[side] ?? <span className="dim">пусто</span>}
                        />
                      </td>
                    ))}
                  </tr>
                ),
              )}
              <tr>
                <td className="muted">Связи</td>
                {(['a', 'b'] as const).map((side) => (
                  <td className="sm" key={side}>
                    {p.links[side].length ? (
                      p.links[side].map((l) => <div key={l}>{l}</div>)
                    ) : (
                      <span className="dim">нет</span>
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          <div className="mnote">
            <Note>
              Связи дубля перейдут сюда. Совпавшие схлопнутся: если оба состояли в одной команде, в составе
              останется одна строка. Лента тоже перейдёт.
            </Note>
          </div>
          {p.notes?.length ? (
            <div className="mnote">
              <Note>
                {p.notes.map((n) => (
                  <div key={n}>{n}</div>
                ))}
              </Note>
            </div>
          ) : null}
          <Option
            checked={ok}
            onToggle={() => setOk(!ok)}
            title="Слияние нельзя отменить"
            description="вторая запись исчезнет, в журнале останется след: что, во что, кем и когда"
          />
        </>
      )}
    </Modal>
  );
}
